import { rueckfrage } from '../../components/Rueckfrage'
import { useAufraeumen, useSettings, useSpeicher } from '../../hooks/queries'
import { groesse } from '../../lib/format'
import { ArchiveIcon, HiddenIcon, PhotoIcon, VideoIcon, WelleIcon } from '../../components/Icons'

/**
 * Wie voll es ist — und was davon an nichts mehr hängt.
 *
 * **Der Grund ist das Paket.** Auf geteiltem Webhosting ist der Speicher das
 * Kontingent, das zuerst ausgeht. `MEDIA_TOTAL_QUOTA_MB` bremst, bevor der
 * Webspace voll ist — aber eine Bremse, die niemand sieht, merkt man erst,
 * wenn sie greift. Aus dem Betrieb gewünscht: „Anzeige des Speicherplatzes,
 * nur für Admins."
 *
 * **Aufgeschlüsselt wird nur, was dem Betrachter gehört** — so gewünscht:
 * „jeder admin sieht das nur für seine Reisen". Die Summe der Anwendung steht
 * trotzdem da, denn sie beantwortet die Frage, um die es geht; alles Übrige
 * ist eine Zahl ohne Namen. Eine Summe verrät, wie voll es ist; ein Titel
 * verriete, was jemand geschrieben hat.
 *
 * **Der Knopf räumt genau das weg, was darüber gezählt wird.** Etwas anderes
 * zu löschen als das Angezeigte wäre eine Überraschung, und Überraschungen
 * beim Löschen sind die schlechteste Sorte.
 */
export function SpeicherCard() {
  const einstellungen = useSettings()
  // Ohne Decke ist dieses Konto keine Verwaltung -- der Server hat
  // entschieden, und danach richtet sich, ob überhaupt gefragt wird.
  const istVerwaltung = einstellungen.data?.ceiling !== undefined
  const speicher = useSpeicher(istVerwaltung)
  const raeumen = useAufraeumen()

  if (!istVerwaltung) return null

  const daten = speicher.data
  const voll = daten === undefined ? 0 : anteil(daten.used_bytes, daten.quota_bytes)

  return (
    <div className="card card--seite">
      <h2 className="card__titel">Speicherplatz</h2>
      <p className="card__lead">
        Fotos, Videos und Sprachnachrichten aller Reisen zusammen. Ist das
        Kontingent erschöpft, nimmt die Anwendung nichts Neues mehr an.
      </p>

      {speicher.isPending && <p className="feld__hinweis">Wird gezählt …</p>}
      {speicher.isError && (
        <p className="notice" role="alert">
          {speicher.error instanceof Error
            ? speicher.error.message
            : 'Das ließ sich nicht ermitteln.'}
        </p>
      )}

      {daten !== undefined && (
        <>
          {/* **Ein Balken und die Zahl daneben.** Der Balken beantwortet „wie
              eng wird es", die Zahl „wie viel genau" -- beides zusammen, weil
              die eine Frage ohne die andere selten gestellt wird. */}
          <div
            className="platz__balken"
            role="img"
            aria-label={`${Math.round(voll * 100)} Prozent belegt`}
          >
            <span
              className={voll > 0.9 ? 'platz__voll platz__voll--eng' : 'platz__voll'}
              style={{ width: `${Math.min(100, voll * 100)}%` }}
            />
          </div>
          <p className="platz__zahl">
            <strong>{groesse(daten.used_bytes)}</strong> von{' '}
            {groesse(daten.quota_bytes)} belegt
            <span className="platz__leise"> · {daten.files} Dateien</span>
          </p>

          {/* Woraus die eigenen Reisen bestehen. Getrennt, weil sie sehr
              verschieden wiegen: ein verkleinertes Foto ist ein paar hundert
              Kilobyte, eine Minute 4K rund 150 MB. */}
          <div className="platz__arten">
            <Art
              zeichen={<PhotoIcon size={16} />}
              name="Fotos"
              wert={daten.by_kind.image}
            />
            <Art
              zeichen={<VideoIcon size={16} />}
              name="Videos"
              wert={daten.by_kind.video}
            />
            <Art
              zeichen={<WelleIcon size={16} />}
              name="Sprache"
              wert={daten.by_kind.audio}
            />
          </div>

          {/* **Wie weit die Vorschaubilder gediehen sind** (#57).

              Aus dem Betrieb: „wie kann ich prüfen, dass das Vorschaubild da
              ist?" Von hier aus geht es, und zwar vom Telefon — die Dateizahl
              oben taugt dafür nicht: sie zählt Zeilen, und ein Vorschaubild
              bekommt keine eigene.

              **Ein Satz und keine Zeile der Tabelle darüber.** Dort steht, was
              etwas wiegt; hier, wie weit etwas gediehen ist. Ohne Fotos steht
              er gar nicht da — eine leere Auskunft ist keine. */}
          {daten.thumbs.total > 0 && (
            <p className="feld__hinweis" style={{ marginTop: 10 }}>
              {daten.thumbs.with >= daten.thumbs.total ? (
                'Alle Fotos haben ein Vorschaubild.'
              ) : (
                <>
                  Vorschaubilder: {daten.thumbs.with} von {daten.thumbs.total} Fotos.{' '}
                  Ältere haben keins — ihre Kachel zeigt weiter das Original.
                </>
              )}
            </p>
          )}

          {/* **Nur die eigenen Reisen mit Namen.** Was anderen gehört, steht
              als eine Zahl darunter -- die Verwaltung sieht von fremden Reisen
              auch sonst nur Zahlen, nie einen Titel. */}
          {daten.trips.length > 0 && (
            <ul className="platz__reisen">
              {daten.trips.map((reise) => (
                <li key={reise.id} className="platz__reise">
                  <span className="platz__name">
                    {reise.title}
                    {reise.hidden && (
                      <HiddenIcon size={13} className="platz__marke" aria-label="zurückgezogen" />
                    )}
                    {!reise.hidden && reise.archived && (
                      <ArchiveIcon size={13} className="platz__marke" aria-label="abgeschlossen" />
                    )}
                  </span>
                  <span className="platz__leise">{reise.files}</span>
                  <span className="platz__viel">{groesse(reise.bytes)}</span>
                  {/* Wie voll *diese* Reise ist -- das eigene Kontingent je
                      Reise steht in den Einstellungen darüber. */}
                  <span className="platz__mini" aria-hidden="true">
                    <span
                      style={{
                        width: `${Math.min(100, anteil(reise.bytes, daten.trip_quota_bytes) * 100)}%`,
                      }}
                    />
                  </span>
                </li>
              ))}
            </ul>
          )}

          {daten.others_bytes > 0 && (
            <p className="platz__zahl platz__leise">
              Reisen anderer: {groesse(daten.others_bytes)}
            </p>
          )}

          {/* **Liegengebliebenes.** Wer beim Schreiben abbricht, lässt Dateien
              zurück; sonst räumt sie der nächste Upload derselben Reise weg —
              was bei einer ruhenden Reise nie passiert. */}
          <p className="platz__zahl" style={{ marginTop: 12 }}>
            {daten.orphans.files === 0 ? (
              'Nichts Liegengebliebenes in deinen Reisen.'
            ) : (
              <>
                {/* Einzahl und Mehrzahl auseinanderhalten: „1 Dateien" liest
                    sich wie ein Fehler, und an einer Stelle, die zum Löschen
                    auffordert, will man keinen Fehler vermuten. */}
                <strong>{daten.orphans.files}</strong>{' '}
                {daten.orphans.files === 1
                  ? 'liegengebliebene Datei'
                  : 'liegengebliebene Dateien'}{' '}
                ({groesse(daten.orphans.bytes)}){' '}
                {daten.orphans.files === 1 ? 'hängt' : 'hängen'} an keiner
                Nachricht und an keinem Punkt.
              </>
            )}
          </p>

          {raeumen.isError && (
            <p className="notice" role="alert">
              {raeumen.error instanceof Error
                ? raeumen.error.message
                : 'Das hat nicht geklappt.'}
            </p>
          )}
          {raeumen.isSuccess && !raeumen.isPending && (
            <p className="feld__hinweis" role="status">
              {raeumen.data.files === 0
                ? 'Es war nichts wegzuräumen.'
                : `${raeumen.data.files} ${raeumen.data.files === 1 ? 'Datei' : 'Dateien'}` +
                  ` weggeräumt, ${groesse(raeumen.data.bytes)} frei.`}
            </p>
          )}

          <button
            className="button button--outline"
            type="button"
            disabled={raeumen.isPending || daten.orphans.files === 0}
            onClick={async () => {
              if (
                await rueckfrage(
                  `${daten.orphans.files} Dateien aus deinen Reisen löschen, die an ` +
                    'keiner Nachricht und an keinem Punkt hängen? Sichtbar ist davon ' +
                    'nichts — es sind Reste abgebrochener Uploads. Rückgängig geht es nicht.',
                  'Wegräumen',
                )
              ) {
                raeumen.mutate()
              }
            }}
          >
            Liegengebliebenes wegräumen
          </button>
        </>
      )}
    </div>
  )
}

/** Eine Zeile der Aufteilung — Zeichen, Name, Anzahl, Größe. */
function Art({
  zeichen,
  name,
  wert,
}: {
  zeichen: React.ReactNode
  name: string
  wert: { files: number; bytes: number }
}) {
  return (
    <p className="platz__art">
      <span className="platz__zeichen">{zeichen}</span>
      <span className="platz__name">{name}</span>
      {/* Die Null bleibt stehen statt zu verschwinden: „keine Videos" ist eine
          Auskunft, eine leere Stelle wäre keine. */}
      <span className="platz__leise">{wert.files}</span>
      <span className="platz__viel">{groesse(wert.bytes)}</span>
    </p>
  )
}

/** Anteil zwischen 0 und 1 — ohne Kontingent gibt es keinen. */
function anteil(teil: number, ganz: number): number {
  return ganz > 0 ? teil / ganz : 0
}
